<template>
  <AuthView title="Forms">
    <InspectFormModal
      v-if="inspectedForm"
      :model-value="!!inspectedForm"
      :form="inspectedForm"
      @update:model-value="inspectedForm = null"
    />
    <CreateOrderModal
      v-if="createOrderForm"
      :model-value="!!createOrderForm"
      :form="createOrderForm"
      @created="createOrderForm = null"
      @update:model-value="createOrderForm = null"
    />
    <v-form :ref="setFormRef">
      <AppRow>
        <VisaCountrySelect v-model="query.country" clearable @click:clear="query.country = undefined" />
        <SearchBox v-model.lazy.trim="query.email" label="Email" clearable />
        <BranchSelect v-model="query.branch" clearable @click:clear="query.branch = undefined" />
        <AppCheckbox :model-value="!!query.payed" label="Payed" @update:model-value="query.payed = $event ? '1' : ''" />
        <AppRow>
          <AppSelect v-model="query.page" :items="buildIncrementalArray(10, 1)" label="Page" />
          <AppSelect v-model="query.count" :items="[25, 50, 100, 250, 500, 750, 1000]" label="Per page" />
        </AppRow>
      </AppRow>
      <AppRow>
        <DatePicker v-model="fromDate" label="From" :min="getYearsAgoIso(1)" past />
        <DatePicker v-model="untilDate" label="Until" :min="getYearsAgoIso(1)" past />
      </AppRow>
      <v-expansion-panels class="mb-6">
        <v-expansion-panel title="📈 Chart">
          <v-expansion-panel-text>
            <AdminFormsChart :forms="forms.slice().reverse()" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-form>
    <LoadingSpinner v-if="loading" />
    <AdminFormsTable v-else
      :forms
      :busy-id
      @inspect="inspect"
      @resume="resume"
      @duplicate="duplicate"
      @send="send"
      @create-order="showCreateOrderModal"
      @delete="del"
    />
  </AuthView>
</template>

<script setup lang="ts">
import { watchThrottled } from '@vueuse/core';
import { snackbarInjectionKey } from '~/assets/injection-keys';
import { getYearsAgoIso, toEndOfDayIso, toStartOfDayIso } from '~/helpers/date';
import { buildIncrementalArray } from '~/helpers/general';
import { deleteForm, duplicateForm, getForms, getResumeToken, sendForm } from '~/helpers/http';
import { buildResumeFormPath } from '~/helpers/routing';
import type { GetFormsOptions, VisaForm, SerializedDb } from '~/types';

definePageMeta({ middleware: 'admin' });

const snackbar = inject(snackbarInjectionKey)!;

const { buildUrl } = useRouting();
const { setFormRef, loading, submit } = useForm();

const busyId = ref<VisaForm['id'] | null>(null);
const inspectedForm = ref<SerializedDb<VisaForm> | null>(null);
const createOrderForm = ref<SerializedDb<VisaForm> | null>(null);
const query = reactive<GetFormsOptions>({
  country: undefined,
  branch: useBranch().code,
  email: '',
  payed: '',
  from: '',
  until: '',
  page: 1,
  count: 25
});

const fromDate = ref('');
const untilDate = ref('');
watch(fromDate, from => { query.from = toStartOfDayIso(from) });
watch(untilDate, until => { query.until = toEndOfDayIso(until) });

const forms = ref(await fetchForms());

watchThrottled(query, refreshForms, { throttle: 2000 });

function inspect(id: VisaForm['id']) {
  inspectedForm.value = findForm(id)!;
}

async function resume(id: VisaForm['id']) {
  busyId.value = id;
  const token = await getResumeToken(id);
  if (!token) {
    throw new Error('Failed getting token');
  }
  const { country } = findForm(id)!;
  const path = buildResumeFormPath(country, token);
  const url = buildUrl(path);
  window.open(url, '_blank');
  busyId.value = null;
}

async function send(id: VisaForm['id']) {
  busyId.value = id;
  const form = forms.value.find(f => f.id == id)!;
  await sendForm(form);
  busyId.value = null;
  snackbar.value!.showSuccess('Sent form to system');
}

function showCreateOrderModal(id: VisaForm['id']) {
  createOrderForm.value = findForm(id)!;
}

async function duplicate(id: VisaForm['id']) {
  busyId.value = id;
  const response = await duplicateForm(id);
  busyId.value = null;
  if (!response) {
    snackbar.value!.showError('Failed to duplicate form');
    return;
  }
  forms.value.unshift(response.newForm);
  snackbar.value!.showSuccess('Duplicated form');
}

function refreshForms() {
  submit(async () => {
    forms.value = await fetchForms();
  }, { skipValidation: true });
}

function fetchForms() {
  return getForms(query);
}

async function del(id: VisaForm['id']) {
  const confirmation = confirm(`Delete form with ID "${id}" ?`);
  if (!confirmation) return;
  busyId.value = id;
  const success = await deleteForm(id);
  busyId.value = null;
  removeForm(id);
  if (success) {
    snackbar.value!.showSuccess(`Deleted form #${id}`);
  }
}

function removeForm(id: VisaForm['id']) {
  forms.value = forms.value.filter(form => form.id != id);
}

function findForm(id: VisaForm['id']) {
  return forms.value.find(form => form.id == id);
}
</script>